import React, { useState } from 'react';
import { ChevronLeft, Phone, Shield, AlertCircle, User, Lock, CheckCircle } from 'lucide-react';

export default function SupplierVerifyPage({ orderData, onBack, onVerified }) {
  const [phoneLast4, setPhoneLast4] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const { supplierInfo } = orderData || {};

  const handleInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPhoneLast4 = [...phoneLast4];
    newPhoneLast4[index] = value.slice(-1);
    setPhoneLast4(newPhoneLast4);
    setError('');

    if (value && index < 3) {
      const nextInput = document.getElementById(`digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    if (newPhoneLast4.every(d => d) && index === 3) {
      handleVerify(newPhoneLast4.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !phoneLast4[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerify = (digits = null) => {
    const code = digits || phoneLast4.join('');
    
    if (code.length !== 4) {
      setError('请输入完整的手机号后四位');
      return;
    }

    setVerifying(true);
    setError('');

    setTimeout(() => {
      const supplierPhoneLast4 = supplierInfo?.phone?.slice(-4);
      if (code === supplierPhoneLast4) {
        onVerified();
      } else {
        setError('验证码不正确，请重新输入');
        setPhoneLast4(['', '', '', '']);
        setVerifying(false);
        document.getElementById('digit-0')?.focus();
      }
    }, 800);
  };

  const isComplete = phoneLast4.every(d => d);

  return (
    <div className="bg-gradient-to-b from-purple-50 to-white min-h-full pb-24">
      <div className="sticky top-0 bg-white/80 backdrop-blur-md z-10 px-4 py-3 border-b border-purple-100 flex items-center">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-purple-50 transition-colors">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">身份验证</span>
      </div>

      <div className="px-6 pt-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-200">
            <Shield size={36} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">验证您的身份</h1>
          <p className="text-slate-500 text-sm">请输入手机号后四位完成身份验证</p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-md">
              <User size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-slate-500 mb-1">验证账户</div>
              <div className="font-bold text-slate-800 text-lg">{supplierInfo?.name}</div>
              <div className="flex items-center gap-2 mt-1">
                <Phone size={14} className="text-slate-400" />
                <span className="text-slate-600 font-mono">
                  {supplierInfo?.phone?.slice(0, 7)}<span className="text-purple-500 font-bold">****</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="relative">
                <input
                  id={`digit-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={phoneLast4[index]}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onFocus={() => setFocusedIndex(index)}
                  disabled={verifying}
                  className={`w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all duration-200 ${
                    phoneLast4[index]
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : focusedIndex === index
                        ? 'border-purple-400 bg-purple-50/50'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                  } ${verifying ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                {phoneLast4[index] && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-purple-500 rounded-full" />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-500 text-sm animate-shake">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {!error && !isComplete && (
            <p className="text-center text-slate-400 text-sm">
              请输入手机号后四位数字
            </p>
          )}

          {!error && isComplete && !verifying && (
            <div className="flex items-center justify-center gap-2 text-green-600 text-sm">
              <CheckCircle size={16} />
              <span>已输入完成，正在验证...</span>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
              <Lock size={16} className="text-blue-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-700 mb-1">安全验证</div>
              <p className="text-xs text-slate-500 leading-relaxed">
                为保障您的账户安全，我们需要验证您的身份。此信息仅用于身份确认，不会被存储或泄露。
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-6">
          <div className="flex items-center gap-2 text-slate-400 text-xs">
            <Shield size={14} />
            <span>数据加密传输</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-xs">
            <Lock size={14} />
            <span>隐私安全保护</span>
          </div>
        </div>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white/80 backdrop-blur-md border-t border-slate-100 px-6 py-4 pointer-events-auto">
          <button
            disabled={!isComplete || verifying}
            onClick={() => handleVerify()}
            className={`w-full py-4 rounded-xl font-bold text-base shadow-lg transition-all duration-300 ${
              isComplete && !verifying
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:shadow-xl active:scale-[0.98]'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
            }`}
          >
            {verifying ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                验证中...
              </span>
            ) : (
              '验证身份'
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
}
